import React, { InvalidEvent, useState } from 'react';
import Select from 'react-select';
import FormElemWrapper from 'components/Generic/Forms/FormFields/FormElemWrapper';
import classNames from 'classnames';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

export type SelectOption<T> = {
  label: string;
  value: T;
};

type AbrdnSelectProps<T> = {
  options: Array<SelectOption<T>>;
  fields?: {
    hint?: Field<string>;
    tooltip?: Field<string>;
  };
  required?: boolean;
  disabled?: boolean;
  title?: string;
  id?: string;
  onChange: (selectedOption: SelectOption<T>) => void;
  placeholder?: string;
  defaultValue?: SelectOption<T>;
  onFocus?: () => void;
  className?: string;
};

const AbrdnSelect = <T extends string | number>({
  options,
  title = '',
  required = false,
  id,
  onChange,
  disabled = false,
  placeholder,
  fields,
  defaultValue,
  className = '',
  onFocus,
}: AbrdnSelectProps<T>) => {
  const [selectedOption, setSelectedOption] = useState<SelectOption<T> | undefined>(defaultValue);
  const [error, setError] = useState({
    isError: false,
    errorMessage: '',
  });

  const handleChange = (selectedOption: SelectOption<T>) => {
    onChange(selectedOption);
    setSelectedOption(selectedOption);
    setError((prevState) => ({
      ...prevState,
      isError: false,
    }));
  };

  const checkError = (event: InvalidEvent<HTMLInputElement>) => {
    if (event.target.validity.valueMissing) {
      setError((prevState) => ({
        ...prevState,
        errorMessage: `Please select ${title}`,
        isError: true,
      }));
    } else {
      setError((prevState) => ({
        ...prevState,
        errorMessage: '',
        isError: false,
      }));
    }
  };

  const CustomIcon = () => {
    return (
      <span className="custom-arrow">
        <span className="custom-arrow__down"></span>
      </span>
    );
  };

  return (
    <FormElemWrapper
      className={classNames('form-element--input', className, {
        'form-element--filled': !!selectedOption,
      })}
      isError={error.isError}
      slots={{
        fieldLabel: title ? <>{title}</> : null,
        tooltip: fields?.tooltip ? <>{fields.tooltip.value}</> : null,
        error: error.isError ? <>{error.errorMessage}</> : null,
        hint: fields?.hint ? <>{fields.hint.value}</> : null,
      }}
      wrapperTag="div"
    >
      {/*
        Select component does not support onInvalid event, therefore we are using input to trigger this event and validate Select
      */}
      <input
        type="text"
        data-id={id}
        required={required}
        value={defaultValue ? defaultValue.value : selectedOption?.value}
        onInvalid={checkError}
        onChange={() => {}}
        aria-hidden={true}
        tabIndex={-1}
        style={{ zIndex: -1, position: 'absolute', left: 0, top: 0, opacity: 0 }}
      />
      <Select
        id={id}
        placeholder={placeholder}
        onChange={handleChange}
        defaultValue={defaultValue}
        required={required}
        options={options}
        aria-label={`select ${title}`}
        aria-errormessage={error.errorMessage}
        aria-live="polite"
        isSearchable={false}
        isDisabled={disabled}
        isOptionDisabled={(option) => option.selected}
        className="abrdn-select field-text"
        classNamePrefix="abrdn-select"
        onFocus={onFocus}
        components={{
          DropdownIndicator: CustomIcon,
        }}
      />
    </FormElemWrapper>
  );
};

export default AbrdnSelect;
